import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { DetailPage, useCapability, useUrlTabState } from '@viq-admin/ui';
import { LeadRulesEditor, LEAD_RULES_COPY, type LeadRulesApi } from '@viq-admin/screens';
import { getLeadRules, saveLeadRules } from '../../api/leadRules';
import { listQuestions } from '../../api/discovery';

// One tab today; the shell is tabbed so later company settings slot in beside it.
const TAB_KEYS = ['lead-rules'];

/**
 * Portal Company settings (spec 2026-09-14 §3.2). company_admin edits ONLY the
 * company's lead rules here — flags and prompts stay staff-only in the console
 * and have no customer-plane write path.
 */
export function CompanySettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, onTabChange] = useUrlTabState(searchParams, setSearchParams, {
    keys: TAB_KEYS,
    defaultKey: 'lead-rules',
  });
  const canEdit = useCapability('manage:LeadRules');
  const api = useMemo<LeadRulesApi>(
    () => ({
      get: () => getLeadRules(),
      save: (input) => saveLeadRules(input),
      listInventoryQuestions: async () => (await listQuestions()).map((q) => ({ id: q.id, text: q.text })),
    }),
    [],
  );

  return (
    <DetailPage
      title="Company settings"
      onNavigate={({ href }) => navigate(href)}
      readView={null}
      tabs={[{ key: 'lead-rules', label: LEAD_RULES_COPY.title, children: <LeadRulesEditor api={api} canEdit={canEdit} /> }]}
      activeTab={activeTab}
      onTabChange={onTabChange}
    />
  );
}
